import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Close,
  Assignment,
  LocationOn,
  Person,
  Schedule,
  AttachMoney,
  DirectionsCar,
  Phone,
  CheckCircle,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface AssignTripDialogProps {
  open: boolean;
  onClose: () => void;
  onAssign: (tripData: TripAssignmentData) => Promise<void>;
  driver: Driver | null;
}

interface Driver {
  id: string;
  personalInfo: {
    firstName: string;
    lastName: string;
    phone: string;
    avatar?: string;
  };
  performance: {
    rating: number;
    totalTrips: number;
  };
}

interface TripAssignmentData {
  driverId: string;
  pickupLocation: string;
  dropoffLocation: string;
  pickupTime: string;
  estimatedDuration: number;
  fareAmount: number;
  customerName: string;
  customerPhone: string;
  tripType: 'economy' | 'luxury' | 'suv' | 'executive';
  specialInstructions?: string;
  priority: 'low' | 'medium' | 'high';
}

const tripTypes = [
  { value: 'economy', label: 'Economy', color: 'default' },
  { value: 'luxury', label: 'Luxury', color: 'warning' },
  { value: 'suv', label: 'SUV', color: 'info' },
  { value: 'executive', label: 'Executive', color: 'error' }
];

const priorities = [
  { value: 'low', label: 'Low Priority', color: 'success' },
  { value: 'medium', label: 'Medium Priority', color: 'warning' },
  { value: 'high', label: 'High Priority', color: 'error' }
];

export default function AssignTripDialog({ 
  open, 
  onClose, 
  onAssign, 
  driver 
}: AssignTripDialogProps) {
  const theme = useTheme();
  
  const [formData, setFormData] = useState<Omit<TripAssignmentData, 'driverId'>>({
    pickupLocation: '',
    dropoffLocation: '',
    pickupTime: '',
    estimatedDuration: 30,
    fareAmount: 0,
    customerName: '',
    customerPhone: '',
    tripType: 'economy',
    specialInstructions: '',
    priority: 'medium'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!driver) return;
    
    if (!formData.pickupLocation || !formData.dropoffLocation || !formData.pickupTime || !formData.customerName) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const tripData: TripAssignmentData = {
        driverId: driver.id,
        ...formData
      };
      
      await onAssign(tripData);
      handleClose();
    } catch (error) {
      console.error('Failed to assign trip:', error);
      alert(`Failed to assign trip: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      pickupLocation: '',
      dropoffLocation: '',
      pickupTime: '',
      estimatedDuration: 30,
      fareAmount: 0,
      customerName: '',
      customerPhone: '',
      tripType: 'economy',
      specialInstructions: '',
      priority: 'medium'
    });
    onClose();
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30); // Minimum 30 minutes from now
    return now.toISOString().slice(0, 16);
  };

  if (!driver) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Assignment color="primary" />
          <Box>
            <Typography variant="h6">Assign Trip</Typography>
            <Typography variant="body2" color="text.secondary">
              Assigning to: {driver.personalInfo.firstName} {driver.personalInfo.lastName}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflow: 'auto' }}>
        <Box sx={{ mt: 2 }}>
          {/* Driver Info Summary */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                src={driver.personalInfo.avatar}
                sx={{ width: 40, height: 40 }}
              >
                {driver.personalInfo.firstName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2">
                  {driver.personalInfo.firstName} {driver.personalInfo.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rating: {driver.performance.rating}/5 • {driver.performance.totalTrips} trips completed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Phone: {driver.personalInfo.phone}
                </Typography>
              </Box>
            </Box>
          </Alert>

          <Grid container spacing={3}>
            {/* Trip Details */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Trip Details</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pickup Location *"
                value={formData.pickupLocation}
                onChange={(e) => setFormData(prev => ({ ...prev, pickupLocation: e.target.value }))}
                placeholder="e.g., Lagos Airport Terminal 1"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LocationOn color="success" /></InputAdornment>
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dropoff Location *"
                value={formData.dropoffLocation}
                onChange={(e) => setFormData(prev => ({ ...prev, dropoffLocation: e.target.value }))}
                placeholder="e.g., Victoria Island Hotel"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LocationOn color="error" /></InputAdornment>
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Pickup Time *"
                value={formData.pickupTime}
                onChange={(e) => setFormData(prev => ({ ...prev, pickupTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: getMinDateTime()
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Schedule /></InputAdornment>
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Estimated Duration (minutes)"
                value={formData.estimatedDuration}
                onChange={(e) => setFormData(prev => ({ ...prev, estimatedDuration: parseInt(e.target.value) }))}
                inputProps={{ min: 15, max: 480 }}
              />
            </Grid>

            {/* Trip Type and Priority */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Trip Type</InputLabel>
                <Select
                  value={formData.tripType}
                  label="Trip Type"
                  onChange={(e) => setFormData(prev => ({ ...prev, tripType: e.target.value as any }))}
                >
                  {tripTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DirectionsCar />
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                >
                  {priorities.map(priority => (
                    <MenuItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Customer Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>Customer Information</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Customer Name *"
                value={formData.customerName}
                onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Person /></InputAdornment>
                }}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Customer Phone"
                value={formData.customerPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>
                }}
              />
            </Grid>

            {/* Fare and Instructions */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Fare Amount (₦)"
                value={formData.fareAmount}
                onChange={(e) => setFormData(prev => ({ ...prev, fareAmount: parseFloat(e.target.value) }))}
                inputProps={{ min: 0, step: 100 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Special Instructions"
                value={formData.specialInstructions}
                onChange={(e) => setFormData(prev => ({ ...prev, specialInstructions: e.target.value }))}
                placeholder="Any special instructions for the driver (e.g., customer preferences, route notes, etc.)"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Trip Summary */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Trip Summary</Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Ready to assign this trip</Typography>
            </Alert>
            <List dense>
              <ListItem>
                <ListItemIcon><LocationOn color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Pickup" 
                  secondary={formData.pickupLocation || 'Not specified'} 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocationOn color="error" /></ListItemIcon>
                <ListItemText 
                  primary="Dropoff" 
                  secondary={formData.dropoffLocation || 'Not specified'} 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Schedule /></ListItemIcon>
                <ListItemText 
                  primary="Pickup Time" 
                  secondary={formData.pickupTime ? new Date(formData.pickupTime).toLocaleString() : 'Not specified'} 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Person /></ListItemIcon>
                <ListItemText 
                  primary="Customer" 
                  secondary={formData.customerName || 'Not specified'} 
                />
              </ListItem>
              {formData.fareAmount > 0 && (
                <ListItem>
                  <ListItemIcon><AttachMoney /></ListItemIcon>
                  <ListItemText 
                    primary="Fare" 
                    secondary={`₦${formData.fareAmount.toLocaleString()}`} 
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.pickupLocation || !formData.dropoffLocation || !formData.pickupTime || !formData.customerName}
          startIcon={<Assignment />}
        >
          {isSubmitting ? 'Assigning Trip...' : 'Assign Trip'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}